import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import {
  ProductGroupPercentages,
  RolloutPlanDetailsStrategy
} from '../../infrastructure/backends/rollout-plan.dto';
import {
  ROLLOUT_PLAN_STEP_CONFIG,
  RolloutPlanStepId
} from '../../infrastructure/types/rollout-plan-form-store.types';

export interface RolloutPlanStepValidationStateType {
  planDetails: boolean;
  targetProductGroups: boolean;
  distributionSettings: boolean;
  summary: boolean;
}

export interface RolloutPlanState {
  currentSection: string;
  stepOrder: string[];
  menuItems: { id: string; label: string }[];
  stepValidation: RolloutPlanStepValidationStateType;
  planDetails: PlanDetailsFormType;
  productGroups: ProductGroupType;
  distributionSettings: DistributionSettingsType;
  errors: RolloutPlanStepErrorsType;
  isEditingRolloutPlan: boolean;
  rolloutPlanStatusInEdit: string;
  rolloutPlanDataForEdit: DistributionSettingsType;
}
export interface PlanDetailsFormType {
  firmwarePackageId: number;
  firmware_package_bundle_version: string;
  isValid: boolean;
}

export interface DistributionSettingsType {
  strategy: DistributionStrategy;
  isValid: boolean;
}

export interface ProductGroupType {
  productGroups: {
    inputIndex: number;
    productGroupId: number;
    name: string;
  }[];
  isValid: boolean;
}
export interface RolloutPlanEditDetailsType {
  packageId: number;
  packageVersion: string;
  productGroupPercentage: ProductGroupPercentages[];
  status: string;
  strategy?: RolloutPlanDetailsStrategy;
}

export interface AllFormDataType {
  planDetails: PlanDetailsFormType;
  productGroups: ProductGroupType;
  distributionSettings: DistributionSettingsType;
}

export interface RolloutPlanStepErrorsType {
  planDetails?: string;
  targetProductGroups?: string;
  distributionSettings?: string | string[];
}

export enum DistributionStrategyType {
  Percentage = 'Percentage',
  Whitelist = 'Whitelist'
}

export type DistributionStrategy =
  | {
      strategyType: DistributionStrategyType.Percentage;
      productGroupPercentages: { productGroupId: number; percentage: number }[];
    }
  | {
      strategyType: DistributionStrategyType.Whitelist;
      btFriendlyNames: string[];
    };

const initialState: RolloutPlanState = {
  currentSection: RolloutPlanStepId.PlanDetails,
  stepOrder: ROLLOUT_PLAN_STEP_CONFIG.map(s => s.id),
  menuItems: ROLLOUT_PLAN_STEP_CONFIG,
  planDetails: { firmware_package_bundle_version: '', firmwarePackageId: 0, isValid: false },
  distributionSettings: {
    strategy: { strategyType: DistributionStrategyType.Percentage, productGroupPercentages: [] },
    isValid: false
  },
  productGroups: { productGroups: [], isValid: false },
  stepValidation: {
    planDetails: false,
    targetProductGroups: false,
    distributionSettings: false,
    summary: false
  },
  errors: {},
  isEditingRolloutPlan: false,
  rolloutPlanStatusInEdit: '',
  rolloutPlanDataForEdit: {
    strategy: {
      strategyType: DistributionStrategyType.Percentage,
      productGroupPercentages: []
    },
    isValid: false
  }
};

export const RolloutPlanStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods(store => ({
    setCurrentSection(section: string) {
      patchState(store, { currentSection: section });
    },

    canNavigateToStep: () => (stepId: string) => {
      const validation = store.stepValidation() || {};
      const order = store.stepOrder() || [];
      const currentIndex = order.indexOf(store.currentSection());
      const targetIndex = order.indexOf(stepId);
      if (targetIndex === -1) return false;
      if (targetIndex <= currentIndex) return true;
      return order.slice(0, targetIndex).every(s => validation[s as keyof typeof validation]);
    },

    goToNextStep() {
      const index = store.stepOrder().indexOf(store.currentSection());
      if (index < store.stepOrder().length - 1) {
        patchState(store, { currentSection: store.stepOrder()[index + 1] });
      }
    },
    goToPreviousStep() {
      const index = store.stepOrder().indexOf(store.currentSection());
      if (index > 0) {
        patchState(store, { currentSection: store.stepOrder()[index - 1] });
      }
    },

    // update the plan details ( firmware package bundle version )
    updatePlanDetails(fields: Partial<PlanDetailsFormType>, error?: string) {
      patchState(store, state => ({
        planDetails: { ...state.planDetails, ...fields },
        stepValidation: {
          ...state.stepValidation,
          planDetails: fields.isValid ?? state.planDetails.isValid
        },
        errors: { ...state.errors, planDetails: error ?? '' }
      }));
    },

    // update target product group and rollout percentage
    updateDistributionSettings(
      fields: Partial<DistributionSettingsType>,
      error?: string | string[]
    ) {
      patchState(store, state => ({
        distributionSettings: { ...state.distributionSettings, ...fields },
        stepValidation: {
          ...state.stepValidation,
          distributionSettings: fields.isValid ?? state.distributionSettings.isValid
        },
        errors: { ...state.errors, distributionSettings: error ?? '' }
      }));
    },

    // update the product groups
    updateProductGroups(fields: Partial<ProductGroupType>, error?: string) {
      patchState(store, state => {
        const updatedProductGroups = fields.productGroups ?? state.productGroups.productGroups;

        const currentStrategy = state.distributionSettings.strategy;
        let reconciledStrategy = currentStrategy;

        if (
          updatedProductGroups.length > 0 &&
          currentStrategy.strategyType === DistributionStrategyType.Percentage
        ) {
          const activeProductGroupIds = new Set(updatedProductGroups.map(pg => pg.productGroupId));
          const percentagesForActiveGroups = currentStrategy.productGroupPercentages.filter(pg =>
            activeProductGroupIds.has(pg.productGroupId)
          );
          const productGroupIdsWithExistingPercentages = new Set(percentagesForActiveGroups.map(pg => pg.productGroupId));
          const defaultPercentagesForNewGroups = updatedProductGroups
            .filter(pg => !productGroupIdsWithExistingPercentages.has(pg.productGroupId))
            .map(pg => ({ productGroupId: pg.productGroupId, percentage: 0 }));

          reconciledStrategy = {
            ...currentStrategy,
            productGroupPercentages: [...percentagesForActiveGroups, ...defaultPercentagesForNewGroups]
          };
        }

        return {
          productGroups: { ...state.productGroups, ...fields },
          distributionSettings: {
            ...state.distributionSettings,
            strategy: reconciledStrategy
          },
          stepValidation: {
            ...state.stepValidation,
            targetProductGroups: fields.isValid ?? state.productGroups.isValid
          },
          errors: { ...state.errors, targetProductGroups: error ?? '' }
        };
      });
    },

    // summary and validation
    updateSummary(isValid: boolean) {
      patchState(store, state => ({
        stepValidation: { ...state.stepValidation, summary: isValid }
      }));
    },

    // set edit mode for the firmware package.
    setEditingRolloutPlan(value: boolean) {
      patchState(store, { isEditingRolloutPlan: value });
    },

    // load rollout plan data for editing
    editRolloutPlan(rolloutPlanDetails: RolloutPlanEditDetailsType) {
      const strategy = rolloutPlanDetails.strategy;
      // Derive product groups from the strategy when available, fall back to productGroupPercentage
      let productGroupsForStore: { inputIndex: number; productGroupId: number; name: string }[];
      let distributionStrategy: DistributionStrategy;

      if (strategy?.strategyType === DistributionStrategyType.Whitelist) {
        const whitelists = strategy.productGroupWhitelists ?? [];
        productGroupsForStore = whitelists.map((pg, index) => ({
          inputIndex: index,
          productGroupId: pg.productGroupId,
          name: pg.productGroupName
        }));
        distributionStrategy = {
          strategyType: DistributionStrategyType.Whitelist,
          btFriendlyNames: whitelists.flatMap(pg => pg.btFriendlyNames)
        };
      } else {
        const percentages =
          strategy?.strategyType === DistributionStrategyType.Percentage
            ? strategy.productGroupPercentages
            : rolloutPlanDetails.productGroupPercentage ?? [];
        productGroupsForStore = percentages.map((pg, index) => ({
          inputIndex: index,
          productGroupId: pg.productGroupId,
          name: pg.productGroupName
        }));
        distributionStrategy = {
          strategyType: DistributionStrategyType.Percentage,
          productGroupPercentages: percentages.map(pg => ({
            productGroupId: pg.productGroupId,
            percentage: pg.percentage
          }))
        };
      }

      patchState(store, {
        planDetails: {
          firmware_package_bundle_version: rolloutPlanDetails.packageVersion,
          firmwarePackageId: rolloutPlanDetails.packageId,
          isValid: true
        },
        distributionSettings: {
          strategy: distributionStrategy,
          isValid: true
        },
        productGroups: {
          productGroups: productGroupsForStore,
          isValid: true
        },
        isEditingRolloutPlan: true,
        rolloutPlanStatusInEdit: rolloutPlanDetails.status
      });
    },

    resetFormData() {
      patchState(store, initialState);
    }
  })),

  withComputed(state => ({
    canNavigateNext: () => {
      const current = state.currentSection();
      const order = state.stepOrder();
      const validation = state.stepValidation();
      return (
        current === order[order.length - 1] || !!validation[current as keyof typeof validation]
      );
    },

    isStepValid: () => (stepId: string) =>
      state.stepValidation()[stepId as keyof RolloutPlanStepValidationStateType] || false,

    isStepDone: () => (stepId: string) => {
      const stepsOrder = state.stepOrder();
      const activeStepIndex = state.stepOrder().indexOf(state.currentSection());
      const stepIndex = stepsOrder.indexOf(stepId);
      if (activeStepIndex > stepIndex) return true;
      return false;
    },
    isFormDirty: () => {
      const validation = state.stepValidation();
      return Object.values(validation).some(isValid => isValid);
    },

    getAllFormData: () => ({
      planDetails: state.planDetails(),
      productGroups: state.productGroups(),
      distributionSettings: state.distributionSettings()
    }),

    mergedProductGroupsWithPercentages: () => {
      const groups = state.productGroups().productGroups;
      const strategy = state.distributionSettings().strategy;

      if (strategy.strategyType !== DistributionStrategyType.Percentage) {
        return [];
      }

      const percentages = strategy.productGroupPercentages;

      const percentageMap = Object.fromEntries(
        percentages.map(productPercentage => [
          productPercentage.productGroupId,
          productPercentage.percentage
        ])
      );

      return groups.map(group => ({
        ...group,
        percentage: percentageMap[group.productGroupId] ?? 0
      }));
    },

    groupedBtFriendlyNames: () => {
      const groups = state.productGroups().productGroups;
      const strategy = state.distributionSettings().strategy;

      if (strategy.strategyType !== DistributionStrategyType.Whitelist) {
        return [];
      }

      const btNames = strategy.btFriendlyNames;

      return groups.map(group => ({
        ...group,
        btFriendlyNames: btNames.filter(name => name.includes(group.name))
      }));
    },

    getError: () => (step: keyof RolloutPlanStepErrorsType) => state.errors()[step] ?? ''
  }))
);
